import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  MapPin,
  Compass,
  ShieldCheck,
  ArrowRight,
  PlusCircle,
  Building2,
  CheckCircle2,
  Layers,
} from 'lucide-react';
import { HeroSection } from '../components/HeroSection';
import { ModernHeroSearchBar } from '../components/ModernHeroSearchBar';
import { TrustFeatures } from '../components/TrustFeatures';
import { HomeSeoContent } from '../components/HomeSeoContent';
import { BrowseCategories } from '../components/BrowseCategories';
import { FeaturedProperties } from '../components/FeaturedProperties';
import { Property, CategoryItem } from '../types';
import { useLanguage } from '../context/LanguageContext';
import { SEOHead } from '../components/SEOHead';
import { buildPropertyUrl, getLocalizedEquivalentUrl, buildCityUrl, buildResourceUrl, slugify } from '../lib/routes';
import { buildHomeJsonLd } from '../lib/jsonLd';
import { PropertyAlertInlineBox } from '../components/PropertyAlertInlineBox';
import { HomeNewsStrip } from '../components/home/HomeNewsStrip';

interface HomePageProps {
  properties: Property[];
  isLoading?: boolean;
  onShowToast?: (msg: string) => void;
}

export const HomePage: React.FC<HomePageProps> = ({
  properties = [],
  isLoading = false,
  onShowToast,
}) => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isFr = language === 'fr';
  const [activeFilter, setActiveFilter] = useState('All');
  const [selectedCityForSearch, setSelectedCityForSearch] = useState<string>('');
  const featuredRef = useRef<HTMLDivElement | null>(null);
  const searchBarRef = useRef<HTMLDivElement | null>(null);

  const handleSelectCityForSearch = (cityName: string) => {
    setSelectedCityForSearch(cityName);
    searchBarRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handleCategorySelect = (cat: CategoryItem) => {
    const icon = cat.icon?.toLowerCase() || '';
    const name = (cat.name || '').toLowerCase();
    let target = '/properties-for-sale';
    if (icon === 'land' || name.includes('land') || name.includes('terrain')) {
      target = '/land-for-sale';
    } else if (icon === 'house' || (name.includes('house') && name.includes('sale')) || name.includes('maison')) {
      target = '/houses-for-sale';
    } else if (icon === 'apartment' || (name.includes('apartment') && name.includes('sale'))) {
      target = '/apartments-for-sale';
    } else if (icon === 'building' || name.includes('commercial')) {
      target = '/commercial-for-sale';
    } else if (icon === 'rent-house' || (name.includes('house') && name.includes('rent'))) {
      target = '/houses-for-rent';
    } else if (name.includes('apartment') && name.includes('rent')) {
      target = '/apartments-for-rent';
    } else if (icon === 'studio' || name.includes('studio')) {
      target = '/studios-for-rent';
    } else if (icon === 'bed' || name.includes('room') || name.includes('chambre')) {
      target = '/rooms-for-rent';
    } else if (icon === 'hotel' || name.includes('hotel')) {
      target = '/hotels';
    } else if (icon === 'motel' || name.includes('motel') || name.includes('auberge')) {
      target = '/motels';
    } else if (icon === 'guesthouse' || name.includes('guesthouse') || name.includes('lodge') || name.includes('meubl')) {
      target = '/guesthouses';
    } else if (icon === 'resort' || name.includes('resort') || name.includes('balnéaire')) {
      target = '/resorts';
    } else if (icon === 'sparkles') {
      target = '/houses-for-sale';
    }
    navigate(getLocalizedEquivalentUrl(target, language));
  };

  const handleTrustFeatureClick = (id: string) => {
    switch (id) {
      case 'verify':
        navigate(isFr ? '/fr/verifier-propriete' : '/en/verify-property');
        break;
      case 'scam':
        navigate(isFr ? '/fr/ressources/anti-scam-checklist' : '/en/resources/anti-scam-checklist');
        break;
      case 'professionals':
        navigate(isFr ? '/fr/professionnels' : '/en/professionals');
        break;
      case 'legal':
        navigate(buildResourceUrl('land-guide', isFr ? 'fr' : 'en'));
        break;
      case 'coverage':
        navigate(isFr ? '/fr/carte' : '/en/map');
        break;
      default:
        navigate(isFr ? '/fr/verifier-propriete' : '/en/verify-property');
        break;
    }
  };

  const quickRegions = [
    { name: 'Littoral', city: 'Douala', id: 'littoral' },
    { name: 'Centre', city: 'Yaoundé', id: 'centre' },
    { name: 'South-West', city: 'Buea & Limbe', id: 'southwest' },
    { name: 'South', city: 'Kribi', id: 'south' },
    { name: 'West', city: 'Bafoussam', id: 'west' },
    { name: 'North', city: 'Garoua', id: 'north' },
  ];

  return (
    <div className="bg-white min-h-screen">
      <SEOHead
        pageKey="home"
        canonicalPath="/"
        jsonLd={buildHomeJsonLd({ lang: isFr ? 'fr' : 'en' })['@graph']}
      />

      {/* 1. Hero Section with Mount Cameroon Background */}
      <HeroSection
        onExploreClick={() => {
          featuredRef.current?.scrollIntoView({ behavior: 'smooth' });
        }}
      />

      {/* Modern Eye-Catching Search Bar (Overlapping Hero Bottom) */}
      <div ref={searchBarRef} className="-mt-12 sm:-mt-16 lg:-mt-20 mb-4 sm:mb-6 relative z-30">
        <ModernHeroSearchBar
          selectedCity={selectedCityForSearch}
          onCitySelected={(city) => setSelectedCityForSearch(city)}
          properties={properties}
        />
      </div>

      {/* 2. Featured Properties (Immediate Above-the-Fold Value) */}
      <div ref={featuredRef}>
        <FeaturedProperties
          properties={properties}
          isLoading={isLoading}
          selectedFilter={activeFilter}
          onFilterChange={setActiveFilter}
          onSelectProperty={(prop) => navigate(buildPropertyUrl(prop, language))}
          onViewAll={() => navigate(isFr ? '/fr/biens-a-vendre' : '/en/properties-for-sale')}
          onOpenMap={() => navigate(isFr ? '/fr/carte' : '/en/map')}
        />
      </div>

      {/* 2b. Latest Real Estate News & Market Intelligence Strip (Directly below main listings) */}
      <HomeNewsStrip />

      {/* 3. Browse by Category */}
      <div className="bg-slate-50/70 border-y border-slate-100">
        <BrowseCategories onSelectCategory={handleCategorySelect} />
      </div>

      {/* 4. Due Diligence & Buyer Protection Strip */}
      <div className="pt-4">
        <TrustFeatures onFeatureClick={handleTrustFeatureClick} />
      </div>

      {/* 5. Interactive Map & Title Verification Section */}
      <section className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="rounded-3xl bg-[#094426] text-white p-7 sm:p-11 shadow-sm border border-emerald-900">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            {/* Left Content */}
            <div className="lg:col-span-7 space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-xs font-semibold text-emerald-100">
                <Compass className="w-3.5 h-3.5 text-amber-300" />
                <span>
                  {isFr
                    ? 'Carte Interactive & Cadastre • 10 Régions'
                    : 'Interactive Property Map • All 10 Regions'}
                </span>
              </div>

              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white leading-tight">
                {isFr
                  ? 'Explorez les terrains et biens par quartier sur la carte'
                  : 'Explore Land & Properties by Neighborhood on the Map'}
              </h2>

              <p className="text-emerald-50/90 text-sm sm:text-base leading-relaxed max-w-xl">
                {isFr
                  ? 'Repérez les parcelles disponibles, comparez les prix par zone et vérifiez les références de Titre Foncier avant toute visite sur le terrain.'
                  : 'Compare available plots by neighborhood, inspect road access, and check registered land title references before scheduling a site visit.'}
              </p>

              {/* Quick Region Pills */}
              <div className="flex flex-wrap gap-2 pt-1">
                {quickRegions.map((reg) => {
                  const cityName = reg.city.split('&')[0].trim();
                  const citySlug = slugify(cityName);
                  const cityUrl = buildCityUrl(citySlug, isFr ? 'fr' : 'en');
                  return (
                    <Link
                      key={reg.id}
                      to={cityUrl}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/10 hover:bg-white/20 text-xs font-medium text-white transition-colors cursor-pointer"
                    >
                      <MapPin className="w-3 h-3 text-amber-300" />
                      <span>{reg.city}</span>
                    </Link>
                  );
                })}
              </div>

              {/* Action Buttons */}
              <div className="pt-2 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => navigate(isFr ? '/fr/carte' : '/en/map')}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white hover:bg-slate-100 text-[#094426] font-bold text-sm shadow-xs transition-all cursor-pointer"
                >
                  <Layers className="w-4 h-4 text-[#094426]" />
                  <span>{isFr ? 'Ouvrir la Carte Interactive' : 'Open Interactive Map'}</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => navigate(isFr ? '/fr/verifier-propriete' : '/en/verify-property')}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold text-sm transition-colors cursor-pointer"
                >
                  <ShieldCheck className="w-4 h-4 text-amber-300" />
                  <span>{isFr ? 'Vérifier un Titre Foncier' : 'Verify a Land Title'}</span>
                </button>
              </div>
            </div>

            {/* Right Clean Checklist Card */}
            <div className="lg:col-span-5">
              <div className="bg-white text-slate-900 rounded-2xl p-6 shadow-md space-y-4">
                <div className="border-b border-slate-100 pb-3">
                  <div className="text-xs font-bold uppercase tracking-wider text-[#094426]">
                    {isFr ? 'Vérification Avant Achat' : 'Buyer Due Diligence'}
                  </div>
                  <div className="text-base font-bold text-slate-900 mt-0.5">
                    {isFr ? '3 étapes clés pour acheter en sécurité' : '3 essential steps before buying land'}
                  </div>
                </div>

                <div className="space-y-3 text-xs sm:text-sm">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                    <div>
                      <span className="font-semibold text-slate-900">{isFr ? 'Contrôle du Titre Foncier : ' : 'Land Title Check: '}</span>
                      <span className="text-slate-600">{isFr ? 'Demandez un certificat de propriété récent au service des domaines.' : 'Request an official certificate of ownership at the land registry.'}</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                    <div>
                      <span className="font-semibold text-slate-900">{isFr ? 'Bornage par Géomètre : ' : 'Boundary Survey: '}</span>
                      <span className="text-slate-600">{isFr ? 'Vérifiez les bornes sur le terrain avec un géomètre assermenté OGEC.' : 'Confirm physical boundary pillars with a licensed OGEC surveyor.'}</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                    <div>
                      <span className="font-semibold text-slate-900">{isFr ? 'Signature devant Notaire : ' : 'Notarial Deed: '}</span>
                      <span className="text-slate-600">{isFr ? 'Effectuez tout règlement uniquement par-devant un notaire agréé.' : 'Complete all payments and deeds strictly through a sworn notary.'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6. Clean Dual Action Section (Sellers & Legal Professionals) */}
      <section className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-6 mb-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Card 1: For Landowners & Sellers */}
          <div className="bg-white border border-slate-200 rounded-2xl p-7 sm:p-8 shadow-2xs hover:shadow-md transition-shadow flex flex-col justify-between">
            <div className="space-y-3">
              <div className="w-11 h-11 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center text-[#094426]">
                <Building2 className="w-5 h-5" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
                {isFr
                  ? 'Vous vendez ou louez un bien au Cameroun ?'
                  : 'Selling or Renting Property in Cameroon?'}
              </h2>
              <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                {isFr
                  ? 'Publiez votre annonce gratuitement et recevez des demandes directes d’acheteurs locaux et de la diaspora sur WhatsApp.'
                  : 'Publish your listing for free and receive direct inquiries from local and diaspora buyers via WhatsApp.'}
              </p>
            </div>

            <div className="pt-6">
              <button
                type="button"
                onClick={() => navigate(isFr ? '/fr/deposer-annonce' : '/en/post-property')}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-[#094426] hover:bg-[#07361e] text-white font-bold text-sm shadow-xs transition-colors cursor-pointer"
              >
                <PlusCircle className="w-4 h-4 text-emerald-300" />
                <span>{isFr ? 'Déposer une annonce gratuite' : 'Post a Free Listing'}</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Card 2: For Notaries, Surveyors & Certified Agents */}
          <div className="bg-white border border-slate-200 rounded-2xl p-7 sm:p-8 shadow-2xs hover:shadow-md transition-shadow flex flex-col justify-between">
            <div className="space-y-3">
              <div className="w-11 h-11 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-800">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
                {isFr
                  ? 'Notaires, géomètres OGEC et agents agréés'
                  : 'Notaries, Sworn Surveyors & Licensed Agents'}
              </h2>
              <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                {isFr
                  ? 'Consultez l’annuaire des professionnels vérifiés ou inscrivez votre cabinet pour accompagner les acquéreurs.'
                  : 'Browse our directory of verified legal and surveying professionals or register your practice to assist buyers.'}
              </p>
            </div>

            <div className="pt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => navigate(isFr ? '/fr/professionnels' : '/en/professionals')}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm shadow-xs transition-colors cursor-pointer"
              >
                <span>{isFr ? 'Voir l’annuaire' : 'Browse Directory'}</span>
                <ArrowRight className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => navigate(isFr ? '/fr/inscription' : '/en/signup')}
                className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold text-sm transition-colors cursor-pointer"
              >
                <span>{isFr ? 'Inscrire mon cabinet' : 'Join as Professional'}</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 6b. WhatsApp & Email Property Alert Radar Banner */}
      <section className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 mb-14">
        <PropertyAlertInlineBox
          city="Douala"
          category="all"
        />
      </section>

      {/* 7. Comprehensive Cameroon Land & Real Estate Advisory Guide */}
      <HomeSeoContent lang={isFr ? 'fr' : 'en'} />
    </div>
  );
};
